/**
 * Deterministic pseudo-random number generator.
 *
 * The whole simulation must be reproducible from a seed: the balance harness
 * runs hundreds of games and needs identical results for identical inputs, and
 * save/load round-trips must not diverge. `Math.random` cannot do either, so
 * nothing in `src/sim` is allowed to call it.
 *
 * Algorithm: mulberry32 - small, fast, and good enough for gameplay.
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    // Force to a 32-bit unsigned integer so a float or negative seed still works.
    this.s = seed >>> 0;
  }

  /** Raw float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  float(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.float(min, max + 1));
  }

  /** True with probability `p`. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Uniform pick. Throws on an empty array so bugs surface immediately. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)] as T;
  }

  /** Weighted pick. Weights must be non-negative and not all zero. */
  weighted<T>(items: readonly T[], weightOf: (item: T) => number): T {
    if (items.length === 0) throw new Error('Rng.weighted: empty array');
    let total = 0;
    for (const item of items) total += Math.max(0, weightOf(item));
    if (total <= 0) return this.pick(items);
    let roll = this.next() * total;
    for (const item of items) {
      roll -= Math.max(0, weightOf(item));
      if (roll <= 0) return item;
    }
    return items[items.length - 1] as T;
  }

  /** Fisher-Yates, returns a new array. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const a = out[i] as T;
      out[i] = out[j] as T;
      out[j] = a;
    }
    return out;
  }

  /** Approximate normal via the mean of 3 uniforms; clamped to +/- 3 sigma. */
  normal(mean: number, stdDev: number): number {
    const u = (this.next() + this.next() + this.next()) / 3;
    return mean + (u - 0.5) * 2 * 1.732 * stdDev;
  }

  /** Snapshot of internal state, for save games. */
  getState(): number {
    return this.s;
  }

  setState(state: number): void {
    this.s = state >>> 0;
  }
}
